/**
 * Transform tab orchestrator.
 *
 * Owns: pipeline-select toolbar, density toggle, save/save-as flow,
 * apply-button wiring, validation hookup, the empty-state vs. populated
 * branch, and the right-rail layout (outline + script preview).
 *
 * The per-step editor cards, drop strips, Add-Step menu, and outline list
 * live under `./internal/*` — see each file for the specific concern it owns.
 */
import React, { useCallback, useMemo, useState } from "react"
import { Dropdown, Select, Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faChevronDown,
  faClone,
  faCompress,
  faExpand,
  faFloppyDisk,
  faPlay,
  faPlus,
  faXmark,
} from "@fortawesome/free-solid-svg-icons"
import { useQueryConsoleLayoutStore } from "stores/queryConsoleLayoutStore"
import ExportModal from "shared/ui/ExportModal/ExportModal.component"
import {
  buildTransformPrefix,
  collectStepIssues,
  TransformStepType,
  useActiveTransformSteps,
  useQueryBuilderStore,
} from "stores/queryBuilderStore"
import { CodebookData } from "types/codebook.types"
import { flattenCodebookForSelect } from "utils/codebookUtils"
import StepList from "./internal/StepList.component"
import StepMenu from "./internal/StepMenu.component"
import PipelineOutline from "./internal/PipelineOutline.component"
import GlossaryButton from "./internal/GlossaryButton.component"
import { CodebookFieldsContext } from "./internal/CodebookContext"

interface TransformViewProps {
  onApply: () => Promise<void>
  isApplying: boolean
  /**
   * Save the active pipeline to the backend under the given name. When the
   * name differs from the pipeline's current display name, the pipeline is
   * also renamed in-store. Resolved when the backend round-trip completes.
   */
  onSaveActivePipeline?: (name: string) => Promise<void>
  /**
   * Delete the active pipeline. The container is responsible for removing the
   * backend record (if previously saved) and the local store entry — the
   * order matters because the backend delete needs the saved name BEFORE the
   * store drops it.
   */
  onDeleteActivePipeline?: () => Promise<void>
  /**
   * Engine-side data tree. Used purely by the validator to recognise paths
   * that already exist in the project (so an `APPENDSTRUCT` targeting an
   * existing field doesn't get falsely flagged as "must exist"). Pass an
   * empty codebook (`[]`) when not yet loaded — the validator just won't
   * have any pre-existing paths to consider.
   */
  codebook?: CodebookData
}

const TransformView: React.FC<TransformViewProps> = ({
  onApply,
  isApplying,
  onSaveActivePipeline,
  onDeleteActivePipeline,
  codebook,
}) => {
  const steps = useActiveTransformSteps()
  const addStep = useQueryBuilderStore((s) => s.addTransformStep)
  const pipelines = useQueryBuilderStore((s) => s.transformPipelines)
  const activePipelineId = useQueryBuilderStore(
    (s) => s.activeTransformPipelineId
  )
  const setActivePipeline = useQueryBuilderStore(
    (s) => s.setActiveTransformPipeline
  )
  const createPipeline = useQueryBuilderStore(
    (s) => s.createTransformPipeline
  )
  const removePipeline = useQueryBuilderStore(
    (s) => s.removeTransformPipeline
  )
  const renamePipeline = useQueryBuilderStore(
    (s) => s.renameTransformPipeline
  )
  const duplicatePipeline = useQueryBuilderStore(
    (s) => s.duplicateTransformPipeline
  )
  const activePipeline = pipelines.find((p) => p.id === activePipelineId)
  const pipelineSavedNames = useQueryBuilderStore((s) => s.pipelineSavedNames)
  const isActivePipelineSaved =
    !!activePipeline &&
    pipelineSavedNames[activePipeline.id] === activePipeline.name
  const [isSavingPipeline, setIsSavingPipeline] = useState(false)
  const [saveAsModalOpen, setSaveAsModalOpen] = useState(false)

  const handleSavePipeline = useCallback(async () => {
    if (!onSaveActivePipeline || !activePipeline) return
    setIsSavingPipeline(true)
    try {
      await onSaveActivePipeline(activePipeline.name)
    } finally {
      setIsSavingPipeline(false)
    }
  }, [activePipeline, onSaveActivePipeline])

  const handleSaveAsConfirm = useCallback(
    async (filename: string) => {
      if (!onSaveActivePipeline || !filename.trim()) return
      setSaveAsModalOpen(false)
      setIsSavingPipeline(true)
      try {
        await onSaveActivePipeline(filename.trim())
      } finally {
        setIsSavingPipeline(false)
      }
    },
    [onSaveActivePipeline]
  )

  const density = useQueryConsoleLayoutStore((s) => s.transformDensity)
  const toggleDensity = useQueryConsoleLayoutStore(
    (s) => s.toggleTransformDensity
  )
  const compact = density === "compact"

  // Per-step expand override only meaningful in compact mode.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const hasApplicable = steps.length > 0

  const previewScript = buildTransformPrefix(steps)
  // Flatten the engine-side data tree once into a path-shaped option list,
  // reused two ways: as a Set of `//`-joined paths for the validator (so a
  // reference to an existing field isn't flagged "must exist"), and as the
  // field suggestions for the CALC wizard (via CodebookFieldsContext below).
  const codebookFields = useMemo(
    () => (codebook ? flattenCodebookForSelect(codebook) : []),
    [codebook]
  )
  const knownExistingPaths = useMemo(
    () => new Set(codebookFields.map((o) => o.value)),
    [codebookFields]
  )
  // Light-weight validator — re-run on every render. The pipeline is tiny
  // (a handful of steps in practice), so memoising it costs more in code
  // weight than we save in cycles.
  const pipelineIssues = collectStepIssues(steps, knownExistingPaths)

  // Empty-state Add Step dropdown — only mounted when `steps.length === 0`
  // so it doesn't collide with the `StepList` internal "Add another step"
  // dropdown that appears once the timeline is populated.
  const [addMenuOpen, setAddMenuOpen] = useState(false)
  const handlePickStep = useCallback(
    (type: TransformStepType) => {
      addStep(type)
      setAddMenuOpen(false)
    },
    [addStep]
  )

  return (
    <CodebookFieldsContext.Provider
      value={{ fields: codebookFields, codebook }}
    >
    <div className="qc-transform-scroll h-full">
      <div className="qc-transform">
        <header className="qc-transform__header">
          <div>
            <h2 className="qc-transform__title">Transform</h2>
            <p className="qc-transform__subtitle">
              Build derived variables and reshape the project tree.
              Steps execute as a pipeline on Apply. New fields and
              containers (QTables) appear in the data tree, ready to
              cross-tab, filter, or distribute.
            </p>
          </div>
          <div className="qc-transform__actions">
            <Tooltip
              title={
                compact
                  ? "Switch to full view: show every field per step"
                  : "Switch to compact view: collapse each step to one line"
              }
              placement="bottom"
            >
              <button
                type="button"
                className="qc-transform__density"
                aria-label="Toggle compact view"
                aria-pressed={compact}
                onClick={toggleDensity}
              >
                <FontAwesomeIcon icon={compact ? faExpand : faCompress} />
              </button>
            </Tooltip>
            <Tooltip
              title={
                hasApplicable
                  ? "Run the transformations and refresh the data tree"
                  : "Add at least one step"
              }
              placement="bottom"
            >
              <span>
                <button
                  type="button"
                  className="qc-transform__apply"
                  onClick={onApply}
                  disabled={!hasApplicable || isApplying}
                >
                  <FontAwesomeIcon icon={faPlay} />
                  <span>{isApplying ? "Applying…" : "Apply"}</span>
                </button>
              </span>
            </Tooltip>
            <GlossaryButton />
          </div>
        </header>

        <div className="qc-transform__pipelines" role="toolbar" aria-label="Pipelines">
          <span className="qc-transform__pipelines-label">Pipeline:</span>
          <Select
            size="small"
            value={activePipelineId ?? undefined}
            onChange={(v) => setActivePipeline(v)}
            options={pipelines.map((p) => ({ value: p.id, label: p.name }))}
            popupMatchSelectWidth={false}
            style={{ minWidth: 160 }}
          />
          {activePipeline && (
            <input
              className="qc-transform__pipelines-name qc-mono"
              type="text"
              value={activePipeline.name}
              onChange={(e) =>
                renamePipeline(activePipeline.id, e.target.value)
              }
              placeholder="Pipeline name"
              aria-label="Rename pipeline"
            />
          )}
          <Tooltip title="New pipeline" placement="bottom">
            <button
              type="button"
              className="qc-transform__pipelines-btn"
              aria-label="Create pipeline"
              onClick={() => createPipeline()}
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </Tooltip>
          <Tooltip title="Duplicate this pipeline" placement="bottom">
            <button
              type="button"
              className="qc-transform__pipelines-btn"
              aria-label="Duplicate pipeline"
              disabled={!activePipelineId}
              onClick={() =>
                activePipelineId && duplicatePipeline(activePipelineId)
              }
            >
              <FontAwesomeIcon icon={faClone} />
            </button>
          </Tooltip>
          <Tooltip title="Delete this pipeline" placement="bottom">
            <button
              type="button"
              className="qc-transform__pipelines-btn qc-transform__pipelines-btn--danger"
              aria-label="Delete pipeline"
              disabled={!activePipelineId}
              onClick={() => {
                if (!activePipelineId) return
                if (onDeleteActivePipeline) {
                  onDeleteActivePipeline().catch((err) => {
                    console.error("Pipeline delete failed:", err)
                  })
                } else {
                  removePipeline(activePipelineId)
                }
              }}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </Tooltip>
          {onSaveActivePipeline && (
            <>
              <span className="qc-action-divider" aria-hidden />
              <Dropdown.Button
                size="small"
                type="primary"
                disabled={!activePipelineId || isSavingPipeline}
                loading={isSavingPipeline}
                onClick={handleSavePipeline}
                menu={{
                  items: [
                    {
                      key: "save-as",
                      label: "Save as new pipeline…",
                      onClick: () => setSaveAsModalOpen(true),
                    },
                  ],
                }}
                buttonsRender={([leftButton, rightButton]) => [
                  <Tooltip
                    key="left"
                    title={
                      isActivePipelineSaved
                        ? "Save (overwrite existing version)"
                        : "Save this pipeline to the project"
                    }
                    placement="bottom"
                  >
                    {leftButton as React.ReactElement}
                  </Tooltip>,
                  rightButton,
                ]}
              >
                <FontAwesomeIcon icon={faFloppyDisk} />
                <span className="qc-transform__save-label">Save</span>
              </Dropdown.Button>
            </>
          )}
        </div>
        <ExportModal
          title="Save pipeline as"
          isVisible={saveAsModalOpen}
          confirm={(filename) => handleSaveAsConfirm(filename)}
          cancel={() => setSaveAsModalOpen(false)}
          message="Choose a name for the new pipeline. Existing saved pipelines with the same name will be overwritten."
          ExportButtonText="Save"
        />

        <div className="qc-transform__body">
          <div className="qc-transform__steps">
            {steps.length === 0 ? (
              <div className="qc-transform__empty">
                <p className="qc-transform__empty-text">
                  No transformation steps yet. Add one to get started.
                </p>
                <Dropdown
                  open={addMenuOpen}
                  onOpenChange={setAddMenuOpen}
                  trigger={["click"]}
                  overlayClassName="qc-step-menu-overlay"
                  placement="bottomLeft"
                  getPopupContainer={(trigger) =>
                    (trigger.closest(".qc-transform-scroll") as HTMLElement) ||
                    document.body
                  }
                  popupRender={() => <StepMenu onPick={handlePickStep} />}
                >
                  <button
                    type="button"
                    className="qc-conditions__add qc-conditions__add--standalone"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    <span>Add step</span>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className="qc-step__caret"
                    />
                  </button>
                </Dropdown>
              </div>
            ) : (
              <StepList
                steps={steps}
                parentLoopId={null}
                scope={[]}
                compact={compact}
                expandedIds={expandedIds}
                toggleExpanded={toggleExpanded}
                allIssues={pipelineIssues}
              />
            )}
          </div>

          <aside className="qc-transform__preview">
            <PipelineOutline steps={steps} issues={pipelineIssues} />
            <div className="qc-preview">
              <div className="qc-preview__header">
                <span className="qc-preview__title">Generated definitions</span>
                <span className="qc-preview__hint">Runs on Apply</span>
              </div>
              <pre className="qc-preview__body qc-mono" aria-readonly="true">
                {previewScript || "# Add a step to see the generated query"}
              </pre>
            </div>
          </aside>
        </div>
      </div>
    </div>
    </CodebookFieldsContext.Provider>
  )
}

export default TransformView
